import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { PhoneCall, MessageCircle } from 'lucide-react';
import { Icon } from '../icons';
import { MODULES, EXTERNAL_URLS } from '../data';
import VoiceCallModal from './VoiceCallModal';

/**
 * VozIA — Fase 4. Bloque navy (oscuro en ambos modos, como el CTA final) que muestra la VOZ IA como
 * diferenciador: los 4 modos reales de voz (entrante, saliente, tiempo real, recepción) + Call Force,
 * cableados a MODULES. Ondas animadas (respeta reduce-motion). Texto blanco blindado con text-[#ffffff]
 * para que el CSS global de día no lo oscurezca. CTA de voz HONESTO: pide una llamada al agente real por
 * WhatsApp (no inventa un número de teléfono).
 */
const MOD_BY_KEY = Object.fromEntries(MODULES.map((m) => [m.key, m]));
const VOICE_KEYS = ['voice_ai_inbound', 'voice_ai_outbound', 'voice_realtime', 'recepcion_ia'];
const BARS = [0.5, 0.9, 0.35, 1, 0.6, 0.85, 0.4, 0.95, 0.55, 0.7];

export default function VozIA() {
  const [callOpen, setCallOpen] = useState(false);
  const voice = VOICE_KEYS.map((k) => MOD_BY_KEY[k]).filter(Boolean);
  const callforce = MOD_BY_KEY['callforce'];

  return (
    <section id="voz-ia" className="relative py-20 md:py-24 overflow-hidden bg-gradient-to-br from-[#0a1424] via-[#0b1730] to-[#0f2f5c] text-[#ffffff]" data-testid="voice-section">
      <style>{`
        @keyframes lcwave { 0%,100% { transform: scaleY(.3); } 50% { transform: scaleY(1); } }
        .lc-bar { animation: lcwave 1.1s ease-in-out infinite; transform-origin: bottom; }
        @media (prefers-reduced-motion: reduce) { .lc-bar { animation: none !important; transform: scaleY(.65); } }
      `}</style>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(124,194,62,.16),transparent_45%),radial-gradient(circle_at_92%_82%,rgba(28,63,116,.5),transparent_55%)]" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        {/* Mensaje + ondas + CTA */}
        <div className="space-y-6">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-lime-300 inline-flex items-center gap-2">
            <PhoneCall className="w-4 h-4" /> Voz IA
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight">
            Tu IA no solo escribe. También <span className="text-lime-300">contesta el teléfono.</span>
          </h2>
          <p className="text-base md:text-lg text-[#ffffff]/80 leading-relaxed">
            Llamadas con voz natural —entrante, saliente y en tiempo real— en <b className="text-[#ffffff]">tu propio número y tu marca</b>, 24/7. No es un widget: es multicanal de verdad.
          </p>

          {/* Ondas de voz */}
          <div className="flex items-end gap-1.5 h-14" aria-hidden="true" data-testid="voice-wave">
            {BARS.map((h, i) => (
              <span
                key={i}
                className="lc-bar w-2 sm:w-2.5 rounded-full bg-lime-400"
                style={{ height: `${h * 100}%`, animationDelay: `${i * 90}ms`, animationDuration: `${900 + (i % 3) * 220}ms` }}
              />
            ))}
          </div>

          <p className="text-sm text-[#ffffff]/70">🎙️ En tu propio número y tu marca — no la nuestra.</p>

          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <Button onClick={() => setCallOpen(true)} className="lc-shine w-full sm:w-auto bg-lime-400 hover:bg-lime-300 text-slate-950 rounded-full px-7 h-12 font-semibold" data-testid="voice-cta-primary">
              <PhoneCall className="w-4 h-4 mr-2" /> Pídele a la IA que te llame
            </Button>
            <a href={EXTERNAL_URLS.whatsapp} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full sm:w-auto border-2 border-[#ffffff]/40 text-[#ffffff] bg-transparent hover:bg-white/10 rounded-full px-7 h-12" data-testid="voice-cta-secondary">
                <MessageCircle className="w-4 h-4 mr-2" /> o escríbele por WhatsApp
              </Button>
            </a>
          </div>
        </div>

        {/* Modos de voz (módulos reales) */}
        <div className="grid sm:grid-cols-2 gap-3">
          {voice.map((m) => (
            <div key={m.key} className="rounded-2xl p-5 bg-white/[0.06] border border-white/10 backdrop-blur-sm" data-testid={`voice-card-${m.key}`}>
              <div className="w-11 h-11 rounded-xl bg-lime-400/15 border border-lime-400/25 text-lime-300 flex items-center justify-center mb-3">
                <Icon name={m.icon} className="w-5 h-5" />
              </div>
              <h3 className="text-base font-semibold">{m.name}</h3>
              <p className="text-sm text-[#ffffff]/65 mt-1.5 leading-relaxed">{m.context}</p>
            </div>
          ))}
          {callforce && (
            <div className="sm:col-span-2 rounded-2xl p-5 bg-lime-400/10 border border-lime-400/30 flex items-start gap-3" data-testid="voice-card-callforce">
              <div className="w-11 h-11 rounded-xl bg-lime-400 text-slate-950 flex items-center justify-center shrink-0">
                <Icon name={callforce.icon} className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold inline-flex items-center gap-2 flex-wrap">
                  {callforce.name} <span className="text-[10px] font-bold uppercase tracking-wide text-lime-300">solo LeadCEM</span>
                </h3>
                <p className="text-sm text-[#ffffff]/70 mt-1 leading-relaxed">{callforce.context}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      <VoiceCallModal open={callOpen} onClose={() => setCallOpen(false)} />
    </section>
  );
}
